import { getAccessToken } from './supabase';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');

export function getApiBaseUrl() {
  return API_BASE_URL;
}

export function getProgressWebSocketUrl(topic, ticket) {
  if (!topic || !ticket) throw new Error('A job-scoped WebSocket ticket is required.');
  const explicit = process.env.REACT_APP_WS_BASE_URL;
  const base = (explicit || API_BASE_URL).replace(/\/$/, '');
  const wsBase = base.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  return `${wsBase}/ws/progress/${encodeURIComponent(topic)}?ticket=${encodeURIComponent(ticket)}`;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function authHeaders() {
  const token = await getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, options = {}) {
  const { timeoutMs = 0, ...fetchOptions } = options;
  const controller = timeoutMs ? new AbortController() : null;
  const timer = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;

  let response;
  try {
    const authorizationHeaders = await authHeaders();
    response = await fetch(`${API_BASE_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...authorizationHeaders,
      ...(fetchOptions.headers || {}),
    },
    ...fetchOptions,
    signal: controller?.signal || fetchOptions.signal,
  });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('The request took too long. Please try again or check the backend terminal.');
    }
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
  }

  if (!response.ok) {
    let message = '';
    try {
      const data = await response.clone().json();
      if (Array.isArray(data.detail)) {
        message = data.detail
          .map((item) => {
            const path = Array.isArray(item.loc) ? item.loc.filter((x) => x !== 'body').join('.') : '';
            return `${path || 'request'}: ${item.msg || JSON.stringify(item)}`;
          })
          .join(' | ');
      } else if (typeof data.detail === 'string') {
        message = data.detail;
      } else if (data.detail) {
        message = JSON.stringify(data.detail);
      } else {
        message = data.message || '';
      }
    } catch (e) {
      message = await response.text();
    }
    const error = new Error(message || `Request failed with status ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export function getAssetUrl(path) {
  if (!path) return '';
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

export async function resolveAssetForDownload(reference) {
  if (!reference) throw new Error('No generated asset is available to download.');
  return request('/api/assets/resolve', {
    method: 'POST',
    body: JSON.stringify({ reference }),
    timeoutMs: 15000,
  });
}

function assetKind(reference, contentType = '') {
  const value = `${reference || ''} ${contentType || ''}`.toLowerCase();
  if (value.includes('.png') || value.includes('image/png')) return 'png';
  if (value.includes('.jpg') || value.includes('.jpeg') || value.includes('image/jpeg')) return 'jpeg';
  if (value.includes('.webp') || value.includes('image/webp')) return 'webp';
  if (value.includes('.mp4') || value.includes('video/mp4')) return 'mp4';
  return '';
}

function hasExpectedAssetSignature(bytes, kind) {
  if (!kind) return true;
  if (kind === 'png') {
    return bytes.length >= 8
      && [137, 80, 78, 71, 13, 10, 26, 10].every((value, index) => bytes[index] === value);
  }
  if (kind === 'jpeg') return bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8;
  if (kind === 'webp') {
    return bytes.length >= 12
      && String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF'
      && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP';
  }
  if (kind === 'mp4') {
    return bytes.length >= 12 && String.fromCharCode(...bytes.slice(4, 8)) === 'ftyp';
  }
  return true;
}

function readBlobPrefix(blob, length) {
  const prefix = blob.slice(0, length);
  if (typeof prefix.arrayBuffer === 'function') {
    return prefix.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('The downloaded file could not be inspected.'));
    reader.readAsArrayBuffer(prefix);
  });
}

export async function fetchAssetForDownload(reference, resolveReference = resolveAssetForDownload) {
  let lastError = null;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const resolved = await resolveReference(reference);
      const response = await fetch(resolved.url, {
        cache: 'no-store',
        credentials: 'omit',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!response.ok) {
        throw new Error(`Asset retrieval failed with status ${response.status}.`);
      }
      const blob = await response.blob();
      const headerSize = Number(response.headers?.get?.('content-length') || 0);
      const expectedSize = Number(resolved.size || headerSize || 0);
      if (blob.size <= 32) {
        throw new Error('The downloaded asset is empty or incomplete.');
      }
      if (expectedSize > 0 && blob.size !== expectedSize) {
        throw new Error(`The downloaded asset is incomplete (${blob.size}/${expectedSize} bytes).`);
      }
      const signature = new Uint8Array(await readBlobPrefix(blob, 16));
      const kind = assetKind(resolved.reference || reference, blob.type || response.headers?.get?.('content-type'));
      if (!hasExpectedAssetSignature(signature, kind)) {
        throw new Error('The downloaded response is not a valid generated media file.');
      }
      return blob;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error('The generated asset could not be downloaded.');
}

export function normalizeCampaignResult(payload) {
  if (!payload || typeof payload !== 'object') return payload || null;

  const isObject = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));
  const isCampaign = (value) => isObject(value) && Boolean(
    value.campaign_title
    || value.creative_assets
    || (value.input && (value.ad_headline || value.image_prompt || value.evaluation))
  );
  const unwrapCampaign = (value, depth = 0) => {
    if (!isObject(value) || depth > 5) return null;
    if (isCampaign(value)) return value;
    for (const key of ['campaign', 'result', 'result_data', 'output', 'data']) {
      const nested = unwrapCampaign(value[key], depth + 1);
      if (nested) return nested;
    }
    return null;
  };
  const wrappedResult = unwrapCampaign(payload);

  const envelopeCandidates = [
    payload.assets,
    payload.result?.assets,
    payload.campaign?.assets,
    payload.job?.assets,
    payload.data?.assets,
  ].filter(isObject);
  const responseCreative = Object.assign(
    {},
    ...envelopeCandidates.map((assets) => (
      isObject(assets.creative)
        ? assets.creative
        : isObject(assets.creative_assets)
          ? assets.creative_assets
          : {}
    )),
  );
  const responseUrls = Object.assign(
    {},
    ...envelopeCandidates.map((assets) => (isObject(assets.urls) ? assets.urls : {})),
  );
  const responseImages = envelopeCandidates.flatMap((assets) => (
    Array.isArray(assets.images) ? assets.images : []
  ));
  const responseVideos = envelopeCandidates.flatMap((assets) => (
    Array.isArray(assets.videos) ? assets.videos : []
  ));
  const mediaReference = (value) => {
    if (typeof value === 'string') return value;
    if (!isObject(value)) return '';
    return value.image_url || value.video_url || value.url || value.reference || '';
  };
  const creative = {
    ...(wrappedResult?.creative_assets && typeof wrappedResult.creative_assets === 'object'
      ? wrappedResult.creative_assets
      : {}),
    ...responseCreative,
  };

  creative.portrait_post_url = creative.portrait_post_url
    || creative.square_ad_url
    || creative.portrait_post_url_relative
    || creative.square_ad_url_relative
    || responseUrls.portrait_post_url
    || mediaReference(responseImages[0])
    || null;
  creative.full_screen_ad_url = creative.full_screen_ad_url
    || creative.full_screen_ad_url_relative
    || responseUrls.full_screen_ad_url
    || mediaReference(responseImages.find((item) => mediaReference(item) !== creative.portrait_post_url))
    || null;
  creative.video_url = creative.video_url
    || creative.video_url_relative
    || responseUrls.video_url
    || mediaReference(responseVideos[0])
    || null;

  const variants = Array.isArray(creative.image_variants)
    ? creative.image_variants
      .map((variant, index) => {
        if (typeof variant === 'string') {
          return {
            id: `generated-${index + 1}`,
            label: `Creative option ${index + 1}`,
            image_url: variant,
          };
        }
        if (!isObject(variant)) return null;
        const imageUrl = variant.image_url
          || variant.image_url_public
          || variant.image_url_relative
          || variant.url
          || variant.reference;
        return imageUrl ? { ...variant, image_url: imageUrl } : null;
      })
      .filter(Boolean)
    : [];
  if (variants.length === 0) {
    [
      ['portrait', 'Portrait creative', creative.portrait_post_url],
      ['full_screen', 'Full-screen creative', creative.full_screen_ad_url],
    ].forEach(([format, label, imageUrl]) => {
      if (imageUrl) {
        variants.push({ id: `${format}-output`, format, label, image_url: imageUrl });
      }
    });
  }
  creative.image_variants = variants;

  if (!wrappedResult && variants.length === 0 && !creative.video_url) return null;
  return {
    ...(wrappedResult || {}),
    id: wrappedResult?.id || payload.job?.id || payload.id || '',
    campaign_title: wrappedResult?.campaign_title || 'Generated campaign',
    input: wrappedResult?.input || {},
    creative_assets: creative,
  };
}

export async function generateCampaign(payload) {
  const data = await request('/api/generate-campaign', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return normalizeCampaignResult(data);
}

export async function startGenerationJob(payload) {
  return request('/api/generate-campaign-async', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchGenerationJob(jobId) {
  return request(`/api/generation-jobs/${jobId}`);
}

export async function fetchGenerationJobAssets(jobId) {
  return request(`/api/generation-jobs/${encodeURIComponent(jobId)}/assets`);
}

export async function cancelGenerationJob(jobId) {
  return request(`/api/generation-jobs/${encodeURIComponent(jobId)}/cancel`, {
    method: 'POST',
    timeoutMs: 20000,
  });
}

export function mergeCampaignResults(basePayload, incomingPayload) {
  const base = normalizeCampaignResult(basePayload);
  const incoming = normalizeCampaignResult(incomingPayload);
  if (!base) return incoming;
  if (!incoming) return base;

  const incomingAssets = Object.fromEntries(
    Object.entries(incoming.creative_assets || {}).filter(([, value]) => (
      value !== null
      && value !== undefined
      && value !== ''
      && (!Array.isArray(value) || value.length > 0)
    )),
  );
  const incomingTitle = incoming.campaign_title;
  const incomingIsAssetEnvelope = incomingTitle === 'Generated campaign'
    && Object.keys(incoming.input || {}).length === 0;
  const campaignTitle = incomingTitle && incomingTitle !== 'Generated campaign'
    ? incomingTitle
    : base.campaign_title || incomingTitle;

  return {
    ...base,
    ...incoming,
    id: incomingIsAssetEnvelope ? (base.id || incoming.id) : (incoming.id || base.id),
    campaign_title: campaignTitle,
    input: {
      ...(base.input || {}),
      ...(incoming.input || {}),
    },
    creative_assets: {
      ...(base.creative_assets || {}),
      ...incomingAssets,
    },
  };
}

function isCompletedJob(job) {
  return String(job?.status || '').toLowerCase() === 'completed';
}

function isFailedJob(job) {
  return String(job?.status || '').toLowerCase() === 'failed';
}

function isCancelledJob(job) {
  return String(job?.status || '').toLowerCase() === 'cancelled';
}

function cancelledJobError(job) {
  const error = new Error('Generation was stopped.');
  error.asyncJobStarted = true;
  error.cancelled = true;
  error.job = job || null;
  return error;
}

export async function waitForGenerationJobPayload(
  jobId,
  {
    maxAttempts,
    maxWaitMs,
    delayMs,
    initialDelayMs = 2500,
    maxDelayMs = 12000,
    backoff = 1.45,
    onUpdate,
    isStopRequested,
  } = {}
) {
  const firstDelay = Math.max(1000, Number(delayMs || initialDelayMs || 2500));
  const deadlineMs = Number(maxWaitMs || (maxAttempts ? maxAttempts * firstDelay : 30 * 60 * 1000));
  const startedAt = Date.now();
  let nextDelay = firstDelay;
  let lastJob = null;

  while (Date.now() - startedAt <= deadlineMs) {
    // Stop polling as soon as the user asks to stop, without waiting for the
    // backend to finish unwinding the render it had already started.
    if (isStopRequested?.()) throw cancelledJobError(lastJob);
    const data = await fetchGenerationJob(jobId);
    const job = data.job;
    lastJob = job;
    if (onUpdate) onUpdate(job, data);
    if (isCompletedJob(job)) return data;
    if (isCancelledJob(job)) throw cancelledJobError(job);
    if (isFailedJob(job)) {
      const error = new Error(job.error || 'Campaign generation failed.');
      error.asyncJobStarted = true;
      error.job = job;
      throw error;
    }
    await sleep(nextDelay);
    nextDelay = Math.min(maxDelayMs, Math.round(nextDelay * Math.max(1.05, backoff)));
  }

  const error = new Error('Campaign generation is still running. Please keep the page open or check the job status later; no second generation was started.');
  error.asyncJobStarted = true;
  error.job = lastJob;
  throw error;
}

export function createProgressWebSocketTicket(jobId) {
  return request(`/api/generation-jobs/${encodeURIComponent(jobId)}/websocket-ticket`, {
    method: 'POST',
  });
}

export async function waitForGenerationJob(jobId, options = {}) {
  const data = await waitForGenerationJobPayload(jobId, options);
  const completedCampaign = normalizeCampaignResult(data);
  try {
    // Refresh the authoritative asset envelope after completion. This avoids a
    // polling race where the final status arrives just before its signed media
    // URLs are reflected in the campaign result returned to the dashboard.
    const assetPayload = await fetchGenerationJobAssets(jobId);
    return mergeCampaignResults(completedCampaign, assetPayload);
  } catch (error) {
    // Older/local backends may not expose the asset-only endpoint. A complete
    // campaign response is still displayable, so do not discard it.
    return completedCampaign;
  }
}

export async function generateCampaignAsync(payload, options = {}) {
  const data = await startGenerationJob(payload);
  const jobId = data?.job?.id;
  if (!jobId) throw new Error('The backend did not return a generation job id.');
  if (options.onJobStart) options.onJobStart(data.job);
  try {
    const campaign = await waitForGenerationJob(jobId, {
      ...options,
      maxWaitMs: options.maxWaitMs ?? 2 * 60 * 60 * 1000,
    });
    if (!campaign) {
      const error = new Error('Generation completed, but the campaign result was not returned. The result can be recovered from the generation job.');
      error.asyncJobStarted = true;
      error.job = { id: jobId, status: 'completed', done: true };
      throw error;
    }
    return campaign;
  } catch (error) {
    error.asyncJobStarted = true;
    throw error;
  }
}

export async function generateExperiment(payload) {
  let asyncStarted = false;
  try {
    const queued = await request('/api/generate-experiment-async', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const jobId = queued?.job?.id;
    if (!jobId) throw new Error('The backend did not return an experiment job id.');
    asyncStarted = true;
    const completed = await waitForGenerationJobPayload(jobId, { maxWaitMs: 60 * 60 * 1000 });
    return completed?.result;
  } catch (error) {
    if (asyncStarted || ![404, 405].includes(Number(error?.status))) throw error;
    return request('/api/generate-experiment', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

export async function saveCampaign(campaign) {
  const data = await request('/api/save-campaign', {
    method: 'POST',
    body: JSON.stringify({ campaign }),
  });
  return normalizeCampaignResult(data);
}

export async function fetchCampaigns() {
  const data = await request('/api/campaigns');
  return {
    ...data,
    campaigns: Array.isArray(data?.campaigns)
      ? data.campaigns.map((campaign) => normalizeCampaignResult(campaign))
      : [],
  };
}

export async function deleteCampaign(campaignId) {
  return request(`/api/campaigns/${encodeURIComponent(campaignId)}`, {
    method: 'DELETE',
  });
}

export async function submitFeedback(payload) {
  return request('/api/feedback', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function submitMediaEvaluation(payload) {
  return request('/api/media-evaluations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function submitContact(payload) {
  return request('/api/contact', {
    method: 'POST',
    body: JSON.stringify(payload),
    timeoutMs: 15000,
  });
}

export async function regenerateImage(campaign, format, prompt) {
  const body = JSON.stringify({ campaign, format, prompt });
  let asyncStarted = false;
  try {
    const queued = await request('/api/regenerate-image-async', {
      method: 'POST',
      body,
    });
    const jobId = queued?.job?.id;
    if (!jobId) throw new Error('The backend did not return an image job id.');
    asyncStarted = true;
    const completed = await waitForGenerationJobPayload(jobId, { maxWaitMs: 30 * 60 * 1000 });
    return normalizeCampaignResult(completed);
  } catch (error) {
    if (asyncStarted || ![404, 405].includes(Number(error?.status))) throw error;
    return request('/api/regenerate-image', {
      method: 'POST',
      body,
    });
  }
}

export async function generativeEdit(campaign, sourceReference, operation, prompt = '', region = null) {
  const body = JSON.stringify({
    campaign,
    source_reference: sourceReference,
    operation,
    prompt,
    region,
  });
  let asyncStarted = false;
  try {
    const queued = await request('/api/generative-edit-async', {
      method: 'POST',
      body,
    });
    const jobId = queued?.job?.id;
    if (!jobId) throw new Error('The backend did not return a generative edit job id.');
    asyncStarted = true;
    const completed = await waitForGenerationJobPayload(jobId, {
      maxWaitMs: 30 * 60 * 1000,
    });
    return completed?.result;
  } catch (error) {
    if (asyncStarted || ![404, 405].includes(Number(error?.status))) throw error;
    return request('/api/generative-edit', {
      method: 'POST',
      body,
    });
  }
}

// ---------------------------------------------------------------------------
// Company Knowledge Base (RAG) API
// ---------------------------------------------------------------------------

async function uploadFile(path, formData) {
  // NOTE: do not set Content-Type; the browser adds the multipart boundary.
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { ...(await authHeaders()) },
    body: formData,
  });
  if (!response.ok) {
    let message = '';
    try {
      const data = await response.clone().json();
      message = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail || data);
    } catch (e) {
      message = await response.text();
    }
    throw new Error(message || `Upload failed with status ${response.status}`);
  }
  return response.json();
}

export async function createCompany(payload) {
  return request('/api/companies', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateCompany(companyId, payload) {
  return request(`/api/companies/${companyId}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function fetchCompanies() {
  return request('/api/companies');
}

export async function fetchCompany(companyId) {
  return request(`/api/companies/${companyId}`);
}

export async function createProduct(payload) {
  return request('/api/products', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateProduct(productId, payload) {
  return request(`/api/products/${productId}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function fetchProduct(productId) {
  return request(`/api/products/${productId}`);
}

export async function fetchProducts(companyId) {
  return request(`/api/companies/${companyId}/products`);
}

export async function fetchDocuments(companyId) {
  return request(`/api/companies/${companyId}/documents`);
}

export async function fetchAssets(companyId) {
  return request(`/api/companies/${companyId}/assets`);
}

export async function uploadLogo(companyId, file) {
  const formData = new FormData();
  formData.append('company_id', companyId);
  formData.append('file', file);
  return uploadFile('/api/upload-logo', formData);
}

export async function deleteLogo(companyId) {
  return request(`/api/companies/${companyId}/logo`, { method: 'DELETE' });
}

export async function createReviewInvitation(campaignId) {
  return request(`/api/campaigns/${encodeURIComponent(campaignId)}/review-invitations`, { method: 'POST', body: JSON.stringify({ expires_in_days: 7 }) });
}
export async function listReviewInvitations(campaignId) {
  return request(`/api/campaigns/${encodeURIComponent(campaignId)}/review-invitations`);
}
export async function revokeReviewInvitation(id) {
  return request(`/api/review-invitations/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export async function openClientReview(token) {
  return request('/api/client-reviews/open', { method: 'POST', body: JSON.stringify({ token }) });
}
export async function submitClientReview(token, accepted, notes = '') {
  return request('/api/client-reviews/feedback', { method: 'POST', body: JSON.stringify({ token, accepted, notes }) });
}

export async function resolveClientReviewAsset(token, reference) {
  return request('/api/client-reviews/assets/resolve', { method: 'POST', body: JSON.stringify({ token, reference }) });
}

export async function deleteLogoAsset(assetId) {
  return request(`/api/logo-assets/${encodeURIComponent(assetId)}`, { method: 'DELETE' });
}

export async function uploadCampaignReference(file, referenceType = 'product') {
  const formData = new FormData();
  formData.append('reference_type', referenceType);
  formData.append('file', file);
  return uploadFile('/api/upload-campaign-reference', formData);
}

export async function uploadAsset(companyId, file, assetType = 'file') {
  const formData = new FormData();
  formData.append('company_id', companyId);
  formData.append('asset_type', assetType);
  formData.append('file', file);
  return uploadFile('/api/upload-asset', formData);
}

export async function uploadDocument(companyId, file) {
  const formData = new FormData();
  formData.append('company_id', companyId);
  formData.append('file', file);
  return uploadFile('/api/upload-document', formData);
}

export async function generateContent(payload) {
  const body = JSON.stringify(payload);
  let asyncStarted = false;
  try {
    const queued = await request('/api/generate-content-async', { method: 'POST', body });
    const jobId = queued?.job?.id;
    if (!jobId) throw new Error('The backend did not return a content job id.');
    asyncStarted = true;
    const completed = await waitForGenerationJobPayload(jobId, { maxWaitMs: 60 * 60 * 1000 });
    return completed?.result;
  } catch (error) {
    if (asyncStarted || ![404, 405].includes(Number(error?.status))) throw error;
    return request('/api/generate-content', { method: 'POST', body });
  }
}

// --- Deletes ---
export async function deleteCompany(companyId) {
  return request(`/api/companies/${companyId}`, { method: 'DELETE' });
}
export async function deleteProduct(productId) {
  return request(`/api/products/${productId}`, { method: 'DELETE' });
}
export async function deleteDocument(documentId) {
  return request(`/api/documents/${documentId}`, { method: 'DELETE' });
}

// --- AI autofill + per-field regenerate ---
export async function suggestCompany(payload) {
  return request('/api/ai/suggest-company', { method: 'POST', body: JSON.stringify(payload) });
}
export async function suggestProduct(payload) {
  return request('/api/ai/suggest-product', { method: 'POST', body: JSON.stringify(payload) });
}
export async function regenerateField(campaign, field, instructions = '') {
  const body = JSON.stringify({ campaign, field, instructions });
  let asyncStarted = false;
  try {
    const queued = await request('/api/regenerate-field-async', { method: 'POST', body });
    const jobId = queued?.job?.id;
    if (!jobId) throw new Error('The backend did not return a field job id.');
    asyncStarted = true;
    const completed = await waitForGenerationJobPayload(jobId, { maxWaitMs: 60 * 60 * 1000 });
    return completed?.result;
  } catch (error) {
    if (asyncStarted || ![404, 405].includes(Number(error?.status))) throw error;
    return request('/api/regenerate-field', { method: 'POST', body });
  }
}

// --- Analytics ---
export async function fetchFeedback() {
  return request('/api/feedback');
}

export async function regenerateVideo(campaign, onUpdate) {
  const body = JSON.stringify({ campaign });
  let started = false;
  try {
    const queued = await request('/api/regenerate-video-async', { method: 'POST', body });
    if (!queued?.job?.id) throw new Error('The backend did not return a video job id.');
    started = true;
    const completed = await waitForGenerationJobPayload(queued.job.id, { maxWaitMs: 2 * 60 * 60 * 1000, onUpdate });
    return completed?.result;
  } catch (error) {
    if (started || ![404, 405].includes(Number(error?.status))) throw error;
    return request('/api/regenerate-video', { method: 'POST', body });
  }
}

export async function retryCopyReview(campaign, onUpdate) {
  const body = JSON.stringify({ campaign });
  let started = false;
  try {
    const queued = await request('/api/retry-copy-review-async', { method: 'POST', body });
    if (!queued?.job?.id) throw new Error('The backend did not return a copy-review job id.');
    started = true;
    const completed = await waitForGenerationJobPayload(queued.job.id, { maxWaitMs: 2 * 60 * 60 * 1000, onUpdate });
    return completed?.result;
  } catch (error) {
    if (started || ![404, 405].includes(Number(error?.status))) throw error;
    return request('/api/retry-copy-review', { method: 'POST', body });
  }
}

export async function uploadProductImage(productId, file) {
  const formData = new FormData();
  formData.append('file', file);
  return uploadFile(`/api/products/${productId}/upload-image`, formData);
}

// --- Authenticated account, preferences, and privacy controls ---
export async function fetchAccount() {
  return request('/api/account');
}

export async function updateAccountProfile(payload) {
  return request('/api/account/profile', { method: 'PATCH', body: JSON.stringify(payload) });
}

export async function fetchAccountPreferences() {
  return request('/api/account/preferences');
}

export async function updateAccountPreferences(payload) {
  return request('/api/account/preferences', { method: 'PATCH', body: JSON.stringify(payload) });
}

export async function fetchTutorialProgress() {
  return request('/api/account/tutorial-progress');
}

export async function updateTutorialProgress(tutorialId, payload) {
  return request(`/api/account/tutorial-progress/${encodeURIComponent(tutorialId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function recordPrivacyConsent(payload) {
  return request('/api/account/privacy-consents', { method: 'POST', body: JSON.stringify(payload) });
}

export async function createSensitiveActionTicket(password, action) {
  return request('/api/account/sensitive-action-ticket', {
    method: 'POST',
    body: JSON.stringify({ password, action }),
  });
}

export async function exportAccountData(ticket = '') {
  return request('/api/account/export', {
    headers: ticket ? { 'X-Sensitive-Action-Ticket': ticket } : {},
  });
}

export async function deleteAccount(ticket = '') {
  return request('/api/account', {
    method: 'DELETE',
    headers: ticket ? { 'X-Sensitive-Action-Ticket': ticket } : {},
    body: JSON.stringify({ confirmation: 'DELETE' }),
  });
}

export async function fetchMediaEvaluations(campaignId, { limit = 200, offset = 0 } = {}) {
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (campaignId) query.set('campaign_id', campaignId);
  return request(`/api/media-evaluations?${query}`);
}

export async function fetchMediaEvaluationSummary() {
  return request('/api/media-evaluations/summary');
}
